/**
 * The device tier: "have I already got this, and did it cost anything?"
 *
 * Stores ASSET KEYS, never URLs (see lib/media.ts for why). The browser HTTP
 * cache holds the bytes; this holds the index. Two levels, one purpose each.
 *
 * Purged wholesale when catalogVersion changes — a re-bake changes the bytes
 * behind the same idea, so a surviving entry would serve stale art forever.
 */
const STORAGE_KEY = 'showling.manifest.v1';
const MAX_ENTRIES = 500;

export type ManifestEntry = { assetKey: string; tier: string; at: number };

type ManifestFile = {
  manifestVersion: 1;
  catalogVersion: string;
  entries: Record<string, ManifestEntry>;
};

function empty(catalogVersion: string): ManifestFile {
  return { manifestVersion: 1, catalogVersion, entries: {} };
}

let file: ManifestFile = load();

function load(): ManifestFile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty('unknown');
    const parsed = JSON.parse(raw) as ManifestFile;
    if (parsed?.manifestVersion !== 1 || typeof parsed.entries !== 'object') {
      return empty('unknown');
    }
    return parsed;
  } catch {
    return empty('unknown');
  }
}

function save(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(file));
  } catch {
    // Quota or private mode: the manifest is an optimisation, never a source
    // of truth. Losing it costs a round trip, not correctness.
  }
}

/** Call once per catalog fetch. Returns true if the manifest was purged. */
export function reconcileCatalogVersion(catalogVersion: string): boolean {
  if (file.catalogVersion === catalogVersion) return false;
  file = empty(catalogVersion);
  save();
  return true;
}

export function remember(key: string, assetKey: string, tier: string): void {
  file.entries[key] = { assetKey, tier, at: Date.now() };
  const keys = Object.keys(file.entries);
  if (keys.length > MAX_ENTRIES) {
    // LRU by last write.
    keys
      .sort((a, b) => (file.entries[a]?.at ?? 0) - (file.entries[b]?.at ?? 0))
      .slice(0, keys.length - MAX_ENTRIES)
      .forEach((k) => delete file.entries[k]);
  }
  save();
}

export function lookup(key: string): ManifestEntry | null {
  return file.entries[key] ?? null;
}

export function stats(): { catalogVersion: string; count: number } {
  return { catalogVersion: file.catalogVersion, count: Object.keys(file.entries).length };
}

export function purge(): void {
  file = empty(file.catalogVersion);
  save();
}
